import { useEffect, useState } from 'react';
import { useAuthStore } from '../store/authStore';
import type { CheckIn, Reward, DailyReward, Task, PointsHistory } from '../types';
import { mockApi } from '../services/mockData';
import { useTranslation } from '../hooks/useTranslation';
import { useModal } from '../hooks/useModal';
import { showTelegramAlert } from '../services/telegramService';
import CustomModal from '../components/CustomModal';
import RewardsHubCard from '../components/rewards/RewardsHubCard';
import LeaderboardCard from '../components/rewards/LeaderboardCard';
import DailyCheckInRewards from '../components/rewards/DailyCheckInRewards';
import TasksSection from '../components/rewards/TasksSection';
import ShopTab from '../components/rewards/ShopTab';
import HistoryTab from '../components/rewards/HistoryTab';
import RewardsTabs from '../components/rewards/RewardsTabs';
import CheckInSkeleton from '../components/skeletons/CheckInSkeleton';

const CheckIn = () => {
  const { user } = useAuthStore();
  const { t, language } = useTranslation();
  const { modal, showAlert, showConfirm, closeModal } = useModal();
  const [checkIns, setCheckIns] = useState<CheckIn[]>([]);
  const [dailyRewards, setDailyRewards] = useState<DailyReward[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [allTasks, setAllTasks] = useState<Task[]>([]); // Store all tasks for counting
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [pointsHistory, setPointsHistory] = useState<PointsHistory[]>([]);
  const [redeemedRewards, setRedeemedRewards] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [mainTab, setMainTab] = useState<'checkin' | 'tasks'>('checkin');
  const [activeTab, setActiveTab] = useState<'tasks' | 'shop' | 'history' | 'mycodes'>('tasks');
  const [taskCategory, setTaskCategory] = useState<'special' | 'social' | 'trading'>('special');
  const [countdown, setCountdown] = useState('00:00:00');
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksHasMore, setTasksHasMore] = useState(false);

  useEffect(() => {
    if (user) {
      loadData();
      startCountdown();
    }
  }, [user]);

  const startCountdown = () => {
    const updateCountdown = () => {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      const diff = tomorrow.getTime() - now.getTime();
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setCountdown(
        `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
      );
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  };

  const loadData = async () => {
    if (!user) return;
    
    try {
      const { checkInApi, rewardApi, pointsHistoryApi } = await import('../services/api');
      const [cis, drs, rws, ph, rdr] = await Promise.all([
        checkInApi.getCheckIns(),
        checkInApi.getDailyRewards(),
        rewardApi.getRewards(),
        pointsHistoryApi.getPointsHistory(),
        rewardApi.getRedeemedRewards(),
      ]);
      
      setCheckIns(cis.checkIns || []);
      setDailyRewards(drs.dailyRewards || []);
      setRewards(rws.rewards || []);
      setPointsHistory(ph.pointsHistory || []);
      setRedeemedRewards(rdr || []);
      
      // Load tasks separately with pagination
      await loadTasks();
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Reload only check-in related data without affecting main loading state
  const reloadCheckInData = async () => {
    if (!user) return;
    
    try {
      const { checkInApi, pointsHistoryApi } = await import('../services/api');
      const [cis, drs, ph] = await Promise.all([
        checkInApi.getCheckIns(),
        checkInApi.getDailyRewards(),
        pointsHistoryApi.getPointsHistory(),
      ]);
      
      setCheckIns(cis.checkIns || []);
      setDailyRewards(drs.dailyRewards || []);
      setPointsHistory(ph.pointsHistory || []);
    } catch (error) {
      console.error('Error reloading check-in data:', error);
    }
  };

  const loadTasks = async () => {
    if (!user || tasksLoading) return;
    
    setTasksLoading(true);
    try {
      const { taskApi } = await import('../services/api');
      // Load all tasks - use a large limit to get all tasks
      const response = await taskApi.getTasks(taskCategory, 1000, 0);
      
      setTasks(response.tasks || []);
      setTasksHasMore(false); // No more to load since we got all
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setTasksLoading(false);
    }
  };

  // Load all tasks for counting (without category filter)
  const loadAllTasksForCount = async () => {
    if (!user) return;
    try {
      const { taskApi } = await import('../services/api');
      // Load all tasks without category filter to get accurate counts
      const response = await taskApi.getTasks(undefined, 1000, 0); // Load a large number to get all
      const allTasksData = response.tasks || [];
      setAllTasks(allTasksData);
      
      // Check if current category has incomplete tasks
      const currentCategoryTasks = allTasksData.filter((t: Task) => t.category === taskCategory);
      const currentHasIncomplete = currentCategoryTasks.some((task: Task) => {
        const isCompleted = task.status === 'claimed' && (task.completionCount || 0) >= (task.maxCompletions || 1);
        return !isCompleted;
      });
      
      // If current category has no incomplete tasks, find first category with incomplete tasks
      if (!currentHasIncomplete) {
        const categories: ('special' | 'social' | 'trading')[] = ['special', 'social', 'trading'];
        for (const category of categories) {
          const categoryTasks = allTasksData.filter((t: Task) => t.category === category);
          const hasIncompleteTask = categoryTasks.some((task: Task) => {
            const isCompleted = task.status === 'claimed' && (task.completionCount || 0) >= (task.maxCompletions || 1);
            return !isCompleted;
          });
          
          if (hasIncompleteTask) {
            setTaskCategory(category);
            // Load tasks for the new category immediately
            const { taskApi } = await import('../services/api');
            const categoryResponse = await taskApi.getTasks(category, 1000, 0);
            setTasks(categoryResponse.tasks || []);
            break;
          }
        }
      }
    } catch (error) {
      console.error('Error loading all tasks for count:', error);
    }
  };

  useEffect(() => {
    if (user && taskCategory) {
      loadTasks();
    }
  }, [taskCategory, user]);

  useEffect(() => {
    if (user) {
      loadAllTasksForCount();
    }
  }, [user]);

  const handleCheckIn = async () => {
    if (!user) return;

    setCheckingIn(true);
    try {
      const response = await mockApi.checkIn(user.id);
      // Update user points immediately
      const { updateUser } = useAuthStore.getState();
      updateUser({ points: response.newPoints || user.points });
      
      // Optimistically update check-ins state
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const newCheckIn: CheckIn = {
        id: response.id,
        userId: response.userId,
        date: today.toISOString(),
        points: response.points,
        day: response.day,
        createdAt: response.createdAt,
      };
      setCheckIns(prev => [newCheckIn, ...prev]);
      
      // Update daily rewards state optimistically
      setDailyRewards(prev => prev.map(reward => 
        reward.day === response.day 
          ? { ...reward, claimed: true }
          : reward
      ));
      
      // Reload check-in data in background to ensure sync
      reloadCheckInData().catch(err => console.error('Error reloading check-in data:', err));
      
      // Reset loading state before showing alert (so button doesn't stay in loading state)
      setCheckingIn(false);
      
      // Show success message
      await showAlert(t('checkIn.checkedInSuccessfully'));
    } catch (error: any) {
      // Reload data to sync state when error occurs (e.g., already checked in)
      reloadCheckInData().catch(err => console.error('Error reloading check-in data:', err));
      
      // Reset loading state before showing alert (so button doesn't stay in loading state)
      setCheckingIn(false);
      
      // Show error message
      await showAlert(error.message || t('checkIn.checkInFailed'));
    }
  };

  const handleClaimTask = async (taskId: string) => {
    if (!user) return;

    try {
      const { taskApi } = await import('../services/api');
      const response = await taskApi.claimTask(taskId);
      
      // Update user points if task was claimed
      if (response && 'newPoints' in response) {
        const { updateUser } = useAuthStore.getState();
        updateUser({ points: response.newPoints || user.points });
        await showAlert(t('checkIn.claimedTask'));
      } else if (response && response.message) {
        // Only show message if it's not "Task is processing" (for social tasks, link already opened)
        if (response.message !== 'Task is processing' && response.message !== 'Task is still processing') {
          await showAlert(response.message);
        }
      }
      loadData();
    } catch (error: any) {
      await showAlert(error.message || t('checkIn.failedToClaimTask'));
    }
  };

  const handleTaskStarted = async (task: Task, _delayMinutes: number) => {
    const taskTitle = task.title;
    const message = `${formatPoints(task.points)} ${t('checkIn.points')}`;
    
    await showAlert(message, taskTitle);
  };


  const handleRedeem = async (rewardId: string) => {
    if (!user) return;

    const reward = rewards.find(r => r.id === rewardId);
    if (!reward) return;

    if (user.points < reward.pointsRequired) {
      await showAlert(t('checkIn.insufficientPoints'));
      return;
    }

    const confirmed = await showConfirm(
      `${reward.name} - ${formatPoints(reward.pointsRequired)} ${t('checkIn.points')}`
    );

    if (confirmed) {
      try {
        const redeemed = await mockApi.redeemReward(user.id, rewardId);
        // Update user points
        const { updateUser } = useAuthStore.getState();
        const { userApi } = await import('../services/api');
        const updatedUser = await userApi.getProfile();
        updateUser({ points: updatedUser.points || user.points });
        await showAlert(
          `${t('checkIn.yourCode')} ${redeemed.code}`
        );
        loadData();
      } catch (error: any) {
        await showAlert(error.message || t('checkIn.redeemFailed'));
      }
    }
  };

  const isCheckedInToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return checkIns.some(c => {
      const checkInDate = new Date(c.date);
      checkInDate.setHours(0, 0, 0, 0);
      return checkInDate.getTime() === today.getTime();
    });
  };

  const getCurrentDay = () => {
    const lastCheckIn = checkIns.sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    )[0];
    return lastCheckIn?.day || 0;
  };

  const formatPoints = (points: number) => {
    // Hiển thị số đầy đủ với dấu phẩy phân cách hàng nghìn
    return points.toLocaleString('en-US');
  };

  if (loading) {
    return <CheckInSkeleton />;
  }

  return (
    <div className="p-4 pb-24">
      <RewardsHubCard
        points={user?.points || 0}
        language={language}
        onRedeemClick={() => setActiveTab('shop')}
        formatPoints={formatPoints}
      />

      {/* Weekly Leaderboard Card */}
      <LeaderboardCard />

      {/* Shop/History Tabs - Only show when activeTab is shop or history */}
      {(activeTab === 'shop' || activeTab === 'history' || activeTab === 'mycodes') && (
        <RewardsTabs
          activeTab={activeTab}
          onTabChange={(tab) => setActiveTab(tab)}
          onBack={() => setActiveTab('tasks')}
        />
      )}

      {activeTab === 'shop' && (
        <ShopTab
          rewards={rewards}
          userPoints={user?.points || 0}
          language={language}
          formatPoints={formatPoints}
          onRedeem={handleRedeem}
          noRewardsText={t('checkIn.noRewards')}
        />
      )}

      {activeTab === 'history' && (
        <HistoryTab
          pointsHistory={pointsHistory}
          language={language}
          formatPoints={formatPoints}
          noHistoryText={t('checkIn.noPointsHistory')}
        />
      )}

      {activeTab === 'mycodes' && (
        <div className="space-y-4">
          <h3 className="text-base font-semibold text-white mb-3">
            {t('checkIn.myRedeemedCodes')}
          </h3>
          {redeemedRewards.length > 0 ? (
            redeemedRewards.map((redeemed) => (
              <div key={redeemed.id} className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl p-5 border border-gray-700/50">
                <div className="flex items-start gap-4 mb-4">
                  {redeemed.rewardImage ? (
                    <img 
                      src={redeemed.rewardImage} 
                      alt={redeemed.rewardName}
                      className="w-16 h-16 rounded-2xl object-cover border-2 border-primary/30"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-yellow-500 flex items-center justify-center shadow-lg">
                      <svg className="w-9 h-9 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                      </svg>
                    </div>
                  )}
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-white mb-1">
                      {redeemed.rewardName}
                    </h4>
                    <div className="text-xs text-gray-400">
                      {new Date(redeemed.redeemedAt).toLocaleString()}
                    </div>
                  </div>
                </div>
                
                <div className="bg-black/30 rounded-xl p-4 border border-primary/20">
                  <div className="text-xs text-gray-400 mb-2">
                    {t('checkIn.yourCode')}
                  </div>
                  <div className="flex items-center justify-between">
                    <code className="text-2xl font-black text-primary tracking-wider">
                      {redeemed.code}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(redeemed.code);
                        showTelegramAlert(t('checkIn.codeCopied'));
                      }}
                      className="w-10 h-10 rounded-xl bg-primary/20 hover:bg-primary/30 flex items-center justify-center transition-all hover:scale-110"
                    >
                      <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl p-12 text-center border border-gray-700/50">
              <div className="w-20 h-20 mx-auto mb-4 bg-gray-700/30 rounded-2xl flex items-center justify-center">
                <svg className="w-12 h-12 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
              </div>
              <p className="text-gray-400">
                {t('checkIn.noRedeemedYet')}
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'tasks' && (
        <>
          {/* Main Tabs - Daily Check-in vs Tasks */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <button
              onClick={() => setMainTab('checkin')}
              className={`relative overflow-hidden py-4 rounded-2xl font-bold transition-all duration-300 ${
                mainTab === 'checkin'
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg shadow-green-500/40 scale-105'
                  : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700/50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{t('checkIn.dailyCheckIn')}</span>
              </div>
            </button>
            
            <button
              onClick={() => setMainTab('tasks')}
              className={`relative overflow-hidden py-4 rounded-2xl font-bold transition-all duration-300 ${
                mainTab === 'tasks'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white shadow-lg shadow-blue-500/40 scale-105'
                  : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700/50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                <span>{t('checkIn.tasks')}</span>
              </div>
            </button>
          </div>

          {mainTab === 'checkin' ? (
            <DailyCheckInRewards
              dailyRewards={dailyRewards}
              currentDay={getCurrentDay()}
              isCheckedInToday={isCheckedInToday()}
              countdown={countdown}
              language={language}
              formatPoints={formatPoints}
              onCheckIn={handleCheckIn}
              checkingIn={checkingIn}
              loadingText={t('common.loading')}
            />
          ) : (
            <TasksSection
              tasks={tasks}
              allTasks={allTasks}
              taskCategory={taskCategory}
              language={language}
              formatPoints={formatPoints}
              onCategoryChange={setTaskCategory}
              onClaimTask={handleClaimTask}
              onTaskStarted={handleTaskStarted}
              onLoadMore={tasksHasMore ? () => loadTasks() : undefined}
              loading={tasksLoading}
            />
          )}
        </>
      )}

      <CustomModal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText={modal.confirmText}
        cancelText={modal.cancelText}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
      />
    </div>
  );
};

export default CheckIn;
